import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";
import { Dropdown } from "../elements/EditDeviceModal";
import EmployeeImageCropModal from "../elements/EmployeeImageCropModal";
import { validateEmployeeImageFile } from "../../utils/employeeImage";

const NO_GROUP_LABEL = "Bez grupe";
const EMPLOYEE_IME_MAX_LENGTH = 50;
const EMPLOYEE_PREZIME_MAX_LENGTH = 50;
const EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH = 4;
const GENERATED_INITIAL_PASSWORD_LENGTH = 8;

const generateInitialPassword = (length = GENERATED_INITIAL_PASSWORD_LENGTH) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const values = new Uint32Array(length);
    crypto.getRandomValues(values);
    return Array.from(values, (value) => chars[value % chars.length]).join('');
};

const normalizeGroupName = (name) => {
    if (name == null || name === "" || name === "null" || name === "undefined") return NO_GROUP_LABEL;
    return name;
};

const groupForApi = (name) => {
    if (name == null || name === "" || name === "null" || name === "undefined") return null;
    const trimmed = String(name).trim();
    if (!trimmed || trimmed.toLowerCase() === NO_GROUP_LABEL.toLowerCase()) return null;
    return trimmed;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getEmployeeFormValues = ({ imeRef, prezimeRef, emailRef, tagIdRef, groupRef, initialPasswordRef }) => ({
    ime: imeRef.current?.value?.trim() || "",
    prezime: prezimeRef.current?.value?.trim() || "",
    username: emailRef.current?.value?.trim() || "",
    tagid: tagIdRef.current?.value?.trim() || "",
    group: groupForApi(groupRef.current),
    ...(initialPasswordRef
        ? { initialPassword: initialPasswordRef.current?.value?.trim() || "" }
        : {}),
});

const getOriginalEmployeeValues = (employee) => ({
    ime: employee?.ime?.trim() || "",
    prezime: employee?.prezime?.trim() || "",
    username: employee?.username?.trim() || "",
    tagid: employee?.tagid?.trim() || "",
    group: groupForApi(employee?.group),
});

const hasEmployeeFormChanges = (formValues, originalValues) =>
    formValues.ime !== originalValues.ime
    || formValues.prezime !== originalValues.prezime
    || formValues.username !== originalValues.username
    || formValues.tagid !== originalValues.tagid
    || formValues.group !== originalValues.group
    || !!(formValues.initialPassword && formValues.initialPassword.length > 0);

const validateEmployeeFormValues = (formValues) => {
    if (!formValues.ime) {
        return "Ime je obavezno.";
    }

    if (formValues.ime.length > EMPLOYEE_IME_MAX_LENGTH) {
        return `Ime može imati najviše ${EMPLOYEE_IME_MAX_LENGTH} karaktera.`;
    }

    if (!formValues.prezime) {
        return "Prezime je obavezno.";
    }

    if (formValues.prezime.length > EMPLOYEE_PREZIME_MAX_LENGTH) {
        return `Prezime može imati najviše ${EMPLOYEE_PREZIME_MAX_LENGTH} karaktera.`;
    }

    if (!isValidEmail(formValues.username)) {
        return "E-Mail adresa nije ispravno uneta.";
    }

    return null;
};

const validateEmployeeCreateFormValues = (formValues) => {
    const baseError = validateEmployeeFormValues(formValues);
    if (baseError) {
        return baseError;
    }

    if (!formValues.initialPassword) {
        return "Privremena lozinka je obavezna.";
    }

    if (formValues.initialPassword.length < EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH) {
        return `Privremena lozinka mora imati najmanje ${EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH} karaktera.`;
    }

    return null;
};

const validateEmployeeResetPinFormValues = (formValues) => {
    if (!formValues.initialPassword) {
        return null;
    }

    if (formValues.initialPassword.length < EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH) {
        return `Privremena lozinka mora imati najmanje ${EMPLOYEE_INITIAL_PASSWORD_MIN_LENGTH} karaktera.`;
    }

    return null;
};

const SettingsView = () => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const employeesData = useAppStore((state) => state.employeesData);
    const loading = useAppStore((state) => state.loading);
    const employeeDeleteSuccessToast = useAppStore((state) => state.employeeDeleteSuccessToast);
    const employeeUpdateSuccessToast = useAppStore((state) => state.employeeUpdateSuccessToast);
    const employeeCreateSuccessToast = useAppStore((state) => state.employeeCreateSuccessToast);

    const [hoveredRowId, setHoveredRowId] = useState(null);
    const [activeRowId, setActiveRowId] = useState(null);
    const isEmployeeFormOpen = useAppStore((state) => state.isEmployeeFormOpen);
    const setIsEmployeeFormOpen = useAppStore((state) => state.setIsEmployeeFormOpen);
    const [employeeFormMode, setEmployeeFormMode] = useState("create");
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const editingEmployee = useMemo(() => {
        if (!isEmployeeFormOpen || employeeFormMode !== "edit" || !selectedEmployee?.user_id) {
            return selectedEmployee;
        }

        const userKey = selectedEmployee.user_id;
        const fromStore = employeesData?.[userKey]
            ?? Object.values(employeesData || {}).find(
                (item) => String(item.user_id) === String(userKey),
            );

        return fromStore || selectedEmployee;
    }, [isEmployeeFormOpen, employeeFormMode, selectedEmployee, employeesData]);

    const activeThemeClass = isDarkTheme ? "dark-color" : "white-color";
    const employees = useMemo(() => {
        if (!employeesData) return [];
        return Object.values(employeesData).sort((a, b) => {
            const aName = `${a?.ime || ""} ${a?.prezime || ""}`.trim();
            const bName = `${b?.ime || ""} ${b?.prezime || ""}`.trim();
            return aName.localeCompare(bName, "sr", { sensitivity: "base" });
        });
    }, [employeesData]);

    const handleEditClick = (employee) => {
        setActiveRowId(employee.user_id);
        setSelectedEmployee(employee);
        setEmployeeFormMode("edit");
        setIsEmployeeFormOpen(true);
    };

    const handleCreateClick = () => {
        setSelectedEmployee(null);
        setEmployeeFormMode("create");
        setIsEmployeeFormOpen(true);
    };

    const handleCloseEmployeeForm = () => {
        setIsEmployeeFormOpen(false);
    };

    const getRowClassName = (userId) => {
        const classes = [];
        if (hoveredRowId === userId) classes.push("st-row-hover");
        if (activeRowId === userId) classes.push("st-row-active");
        return classes.join(" ");
    };

    const getRowHandlers = (employee) => ({
        onMouseEnter: () => setHoveredRowId(employee.user_id),
        onMouseLeave: () => setHoveredRowId(null),
        onClick: () => handleEditClick(employee),
    });

    const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
    const tableClass = `kv-table ${isDarkTheme ? "dark-table" : ""}`;

    return (
        <div className="view-container">
            <div className="view-header-container">
                <div className="view-header-top">
                    <div className={`v-header-name ${activeThemeClass}`}>Podešavanja</div>
                    <div className={`v-header-description ${isDarkTheme ? "dark-color" : ""}`}>Obuhvaćeno je dodavanje novih korisnika, brisanje ili izmene podataka postojećih. Novi tag se dodeljuje korisniku tako što se približi čitaču, dok je pokrenuta forma za dodavanje zaposlenih ili izmene. Ako tag do tada nije bio evidentiran, prikazaće se u odgovarajućem polju.</div>
                </div>
            </div>
            <div className="settings-content-container">
                <div className="settings-section-1-container">
                    <div className="st-employee-div">
                        <div className="st-header-row">
                            <h2 className={`st-employee-title ${activeThemeClass}`}>Zaposleni</h2>
                            <button
                                type="button"
                                className={`cv-new-absence-button st-add-employee-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                onClick={handleCreateClick}
                            >
                                + Dodaj zaposlenog
                            </button>
                        </div>
                        <div className="kv-table-container st-table-shell">
                            <div className="st-table-scroll">
                                <table className={`${tableClass} st-employee-table-main`}>
                                    <thead>
                                        <tr>
                                            <th className="st-avatar-col"> </th>
                                            <th>Ime</th>
                                            <th>Prezime</th>
                                            <th className="st-email-col">E-Mail</th>
                                            <th>Tag ID</th>
                                            <th>Grupa</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!loading && employees.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="st-empty-row">
                                                    Nema zaposlenih za prikaz.
                                                </td>
                                            </tr>
                                        )}
                                        {employees.map((employee) => (
                                            <tr
                                                key={employee.user_id}
                                                className={getRowClassName(employee.user_id)}
                                                {...getRowHandlers(employee)}
                                            >
                                                <td className="st-avatar-col">
                                                    <img
                                                        src={`${baseImgUrl}/employees/${employee.image}`}
                                                        alt=""
                                                        className={`st-employee-avatar ${isDarkTheme ? "dark-border" : ""}`}
                                                        crossOrigin="use-credentials"
                                                        onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = "/views/login.svg";
                                                        }}
                                                    />
                                                </td>
                                                <td>{employee.ime || "-"}</td>
                                                <td>{employee.prezime || "-"}</td>
                                                <td className="st-email-col">{employee.username || "-"}</td>
                                                <td>{employee.tagid || "-"}</td>
                                                <td>{employee.group || "Bez grupe"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="st-table-actions">
                                <table className={`${tableClass} st-actions-table`}>
                                    <thead>
                                        <tr>
                                            <th className="asv-emp-day-logs-table-col-action"> </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!loading && employees.length === 0 && (
                                            <tr>
                                                <td className="asv-emp-day-logs-table-col-action" />
                                            </tr>
                                        )}
                                        {employees.map((employee) => (
                                            <tr
                                                key={employee.user_id}
                                                className={getRowClassName(employee.user_id)}
                                                {...getRowHandlers(employee)}
                                            >
                                                <td className="asv-emp-day-logs-table-col-action">
                                                    <button
                                                        type="button"
                                                        className="asv-emp-day-log-edit-btn"
                                                        aria-label={`Izmeni zaposlenog ${employee.ime || ""} ${employee.prezime || ""}`.trim()}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditClick(employee);
                                                        }}
                                                    >
                                                        <img
                                                            className={`dv-card-edit-tool-btn ${isDarkTheme ? "dark-filter" : ""}`}
                                                            src="/cards/Edit.svg"
                                                            alt=""
                                                        />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AnimatePresence>
                {employeeDeleteSuccessToast && <EmployeeDeleteSuccessToast key="employee-delete-toast" />}
                {employeeUpdateSuccessToast && <EmployeeUpdateSuccessToast key="employee-update-toast" />}
                {employeeCreateSuccessToast && <EmployeeCreateSuccessToast key="employee-create-toast" />}
                {isEmployeeFormOpen && (
                    <EmployeeFormModal
                        key={editingEmployee?.user_id ?? "create"}
                        mode={employeeFormMode}
                        employee={editingEmployee}
                        onClose={handleCloseEmployeeForm}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default SettingsView;

const EmployeeDeleteSuccessToast = () => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const setEmployeeDeleteSuccessToast = useAppStore((state) => state.setEmployeeDeleteSuccessToast);

    const dismiss = () => setEmployeeDeleteSuccessToast(false);

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000);
        return () => clearTimeout(timer);
    }, []);

    const content = (
        <motion.div
            className={`dv-device-toast-container ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "none" }}
            >
                Korisnik uspešno obrisan
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

const EmployeeUpdateSuccessToast = () => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const setEmployeeUpdateSuccessToast = useAppStore((state) => state.setEmployeeUpdateSuccessToast);

    const dismiss = () => setEmployeeUpdateSuccessToast(false);

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000);
        return () => clearTimeout(timer);
    }, []);

    const content = (
        <motion.div
            className={`dv-device-toast-container ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "none" }}
            >
                Podaci zaposlenog uspešno sačuvani
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

const EmployeeCreateSuccessToast = () => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const setEmployeeCreateSuccessToast = useAppStore((state) => state.setEmployeeCreateSuccessToast);

    const dismiss = () => setEmployeeCreateSuccessToast(false);

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000);
        return () => clearTimeout(timer);
    }, []);

    const content = (
        <motion.div
            className={`dv-device-toast-container ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "none" }}
            >
                Zaposleni uspešno dodat
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

const EmployeeFormModal = ({ mode, employee, onClose }) => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const deviceGroupsState = useAppStore((state) => state.deviceGroupsState);
    const groupsState = useAppStore((state) => state.groupsState);
    const deleteEmployee = useAppStore((state) => state.deleteEmployee);
    const updateEmployee = useAppStore((state) => state.updateEmployee);
    const createEmployee = useAppStore((state) => state.createEmployee);
    const scannedUnknownTagId = useAppStore((state) => state.scannedUnknownTagId);
    const scannedUnknownTagTick = useAppStore((state) => state.scannedUnknownTagTick);

    const modalContainerRef = useRef(null);
    const imageInputRef = useRef(null);
    const imeRef = useRef(null);
    const prezimeRef = useRef(null);
    const emailRef = useRef(null);
    const tagIdRef = useRef(null);
    const initialPasswordRef = useRef(null);
    const groupRef = useRef(normalizeGroupName(employee?.group));

    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [addingNewItemId, setAddingNewItemId] = useState(null);
    const [formError, setFormError] = useState("");
    const [tagIdFlash, setTagIdFlash] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [pendingImagePreview, setPendingImagePreview] = useState(null);
    const [pendingImageBlob, setPendingImageBlob] = useState(null);

    const isEditMode = mode === "edit";
    const showPendingAccountSetupPin = isEditMode && employee?.pendingAccountSetup === true;
    const TAG_ID_CHAR_MS = 38;
    const TAG_ID_FLASH_MS = 520;
    const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;

    const groupOptions = useMemo(() => {
        const names = new Set([NO_GROUP_LABEL]);

        Object.keys(deviceGroupsState || {}).forEach((name) => {
            names.add(normalizeGroupName(name));
        });
        Object.keys(groupsState || {}).forEach((name) => {
            names.add(normalizeGroupName(name));
        });

        if (employee?.group) names.add(normalizeGroupName(employee.group));
        if (groupRef.current) names.add(normalizeGroupName(groupRef.current));

        return Array.from(names)
            .filter(Boolean)
            .sort((a, b) => {
                if (a === NO_GROUP_LABEL) return 1;
                if (b === NO_GROUP_LABEL) return -1;
                return a.localeCompare(b, "sr", { sensitivity: "base" });
            });
    }, [deviceGroupsState, groupsState, employee?.group]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isDeleteConfirmOpen || isCropModalOpen) return;
            if (modalContainerRef.current?.contains(e.target)) return;
            onClose();
        };

        document.addEventListener("mousedown", handleClickOutside, true);
        return () => document.removeEventListener("mousedown", handleClickOutside, true);
    }, [onClose, isDeleteConfirmOpen, isCropModalOpen]);

    useEffect(() => () => {
        if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    }, [cropImageSrc, pendingImagePreview]);

    useEffect(() => {
        if (!scannedUnknownTagTick || !scannedUnknownTagId) return undefined;

        const tag = String(scannedUnknownTagId).trim();
        if (!tag || !tagIdRef.current) return undefined;

        let index = 0;
        let flashTimer;
        tagIdRef.current.value = "";
        setTagIdFlash(false);
        setFormError("");

        const interval = window.setInterval(() => {
            index += 1;
            if (tagIdRef.current) {
                tagIdRef.current.value = tag.slice(0, index);
            }

            if (index >= tag.length) {
                window.clearInterval(interval);
                setTagIdFlash(true);
                flashTimer = window.setTimeout(() => setTagIdFlash(false), TAG_ID_FLASH_MS);
            }
        }, TAG_ID_CHAR_MS);

        return () => {
            window.clearInterval(interval);
            if (flashTimer) window.clearTimeout(flashTimer);
        };
    }, [scannedUnknownTagTick, scannedUnknownTagId]);

    const handleGroupChange = (value) => {
        groupRef.current = normalizeGroupName(value);
        clearFormError();
    };

    const clearFormError = () => {
        if (formError) {
            setFormError("");
        }
    };

    const handleGenerateInitialPassword = () => {
        if (!initialPasswordRef.current) return;
        initialPasswordRef.current.value = generateInitialPassword();
        clearFormError();
    };

    const handleAvatarClick = () => {
        imageInputRef.current?.click();
    };

    const handleImageFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";

        if (!file) return;

        const validationError = await validateEmployeeImageFile(file);
        if (validationError) {
            setFormError(validationError);
            return;
        }

        clearFormError();

        if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
        const nextCropSrc = URL.createObjectURL(file);
        setCropImageSrc(nextCropSrc);
        setIsCropModalOpen(true);
    };

    const handleCropCancel = () => {
        setIsCropModalOpen(false);
        if (cropImageSrc) {
            URL.revokeObjectURL(cropImageSrc);
            setCropImageSrc(null);
        }
    };

    const handleCropConfirm = (blob) => {
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);

        const previewUrl = URL.createObjectURL(blob);
        setPendingImageBlob(blob);
        setPendingImagePreview(previewUrl);
        setIsCropModalOpen(false);

        if (cropImageSrc) {
            URL.revokeObjectURL(cropImageSrc);
            setCropImageSrc(null);
        }
    };

    const handleConfirm = async () => {
        if (isSubmitting) return;

        const formValues = getEmployeeFormValues({
            imeRef,
            prezimeRef,
            emailRef,
            tagIdRef,
            groupRef,
            ...((!isEditMode || showPendingAccountSetupPin) ? { initialPasswordRef } : {}),
        });

        if (isEditMode) {
            const originalValues = getOriginalEmployeeValues(employee);

            if (!hasEmployeeFormChanges(formValues, originalValues) && !pendingImageBlob) {
                setFormError("Nema izmena za čuvanje.");
                return;
            }

            const validationError = validateEmployeeFormValues(formValues)
                || validateEmployeeResetPinFormValues(formValues);
            if (validationError) {
                setFormError(validationError);
                return;
            }

            setFormError("");
            setIsSubmitting(true);

            try {
                const success = await updateEmployee({
                    user_id: employee.user_id,
                    ...formValues,
                    ...(pendingImageBlob ? { image: pendingImageBlob } : {}),
                    ...(formValues.initialPassword ? { initialPassword: formValues.initialPassword } : {}),
                });

                if (success === true) {
                    onClose();
                    return;
                }

                setFormError(typeof success === 'string' ? success : 'Čuvanje nije uspelo. Pokušajte ponovo.');
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        const validationError = validateEmployeeCreateFormValues(formValues);
        if (validationError) {
            setFormError(validationError);
            return;
        }

        setFormError("");
        setIsSubmitting(true);

        try {
            const success = await createEmployee({
                ...formValues,
                ...(pendingImageBlob ? { image: pendingImageBlob } : {}),
            });

            if (success === true) {
                onClose();
                return;
            }

            setFormError(typeof success === 'string' ? success : 'Dodavanje nije uspelo. Pokušajte ponovo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = () => {
        if (!employee?.user_id) return;
        setIsDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!employee?.user_id) return;

        const success = await deleteEmployee(employee.user_id);
        setIsDeleteConfirmOpen(false);

        if (success) {
            onClose();
        }
    };

    const title = isEditMode ? "Izmena zaposlenog" : "Dodavanje zaposlenog";
    const subtitle = isEditMode
        ? `${employee?.ime || ""} ${employee?.prezime || ""}`.trim()
        : "Unesite podatke novog zaposlenog";

    const hasServerImage = Boolean(employee?.image) && !pendingImagePreview;
    const hasDisplayImage = Boolean(pendingImagePreview) || hasServerImage;
    const displayImageSrc = pendingImagePreview
        ? pendingImagePreview
        : hasServerImage
            ? `${baseImgUrl}/employees/${employee.image}`
            : null;
    const avatarActionLabel = hasDisplayImage ? "Izmeniti sliku" : "Dodati sliku";

    const modalContent = (
        <>
            <motion.div
                className="dv-edit-modal-container st-employee-form-modal-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, delay: 0.14 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: "none" }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`dv-edit-modal st-employee-form-modal ${isDarkTheme ? "dark-dv-edit-modal" : "white-dv-edit-modal"}`}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    style={{ pointerEvents: "auto" }}
                >
                    <button
                        type="button"
                        className={`dv-edit-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                        onClick={onClose}
                        aria-label="Zatvori formu"
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ display: "block", margin: "auto" }}
                        >
                            <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </button>

                    <h3>
                        <img src="/sidebar/Podesavanja.svg" alt="" className={isDarkTheme ? "dark-filter" : ""} />
                        {title}
                    </h3>

                    <div className="st-employee-form-avatar-wrap">
                        <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="st-employee-form-avatar-input"
                            onChange={handleImageFileChange}
                        />
                        <button
                            type="button"
                            className="st-employee-form-avatar-btn"
                            aria-label={avatarActionLabel}
                            onClick={handleAvatarClick}
                        >
                            {displayImageSrc ? (
                                <img
                                    src={displayImageSrc}
                                    alt=""
                                    className="st-employee-form-avatar"
                                    crossOrigin={pendingImagePreview ? undefined : "use-credentials"}
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = "/views/login.svg";
                                    }}
                                />
                            ) : (
                                <div className="st-employee-form-avatar st-employee-form-avatar--placeholder">
                                    <img
                                        src="/views/login.svg"
                                        alt=""
                                        className="st-employee-form-avatar-placeholder-icon"
                                    />
                                </div>
                            )}
                            <span className="st-employee-form-avatar-overlay">
                                <img
                                    className={`st-employee-form-avatar-edit-icon ${isDarkTheme ? "dark-filter" : ""}`}
                                    src="/cards/Edit.svg"
                                    alt=""
                                />
                                <span className="st-employee-form-avatar-overlay-label">{avatarActionLabel}</span>
                            </span>
                        </button>
                    </div>

                    <div className="dv-edit-settings-container st-employee-form-fields">
                        <div className="dv-edit-field">
                            <label>Ime:</label>
                            <input
                                ref={imeRef}
                                type="text"
                                className="st-employee-form-input"
                                defaultValue={employee?.ime || ""}
                                placeholder="Unesite ime"
                                maxLength={EMPLOYEE_IME_MAX_LENGTH}
                                onChange={clearFormError}
                            />
                        </div>

                        <div className="dv-edit-field">
                            <label>Prezime:</label>
                            <input
                                ref={prezimeRef}
                                type="text"
                                className="st-employee-form-input"
                                defaultValue={employee?.prezime || ""}
                                placeholder="Unesite prezime"
                                maxLength={EMPLOYEE_PREZIME_MAX_LENGTH}
                                onChange={clearFormError}
                            />
                        </div>

                        <div className="dv-edit-field">
                            <label>E-Mail:</label>
                            <input
                                ref={emailRef}
                                type="email"
                                className="st-employee-form-input"
                                defaultValue={employee?.username || ""}
                                placeholder="ime.prezime@domen.rs"
                                onChange={clearFormError}
                            />
                        </div>

                        {!isEditMode && (
                            <div className="dv-edit-field st-tagid-field">
                                <label>Privremena lozinka:</label>
                                <input
                                    ref={initialPasswordRef}
                                    type="password"
                                    className="st-employee-form-input"
                                    placeholder="Unesite lozinku za prvu prijavu"
                                    autoComplete="new-password"
                                    onChange={clearFormError}
                                />
                                <div className="st-employee-form-tagid-hint">
                                    Služi samo za prvu prijavu. Zaposleni će pri prvom ulasku postaviti svoju lozinku i prihvatiti opšte uslove korišćenja.
                                </div>
                            </div>
                        )}

                        {showPendingAccountSetupPin && (
                            <div className="dv-edit-field st-tagid-field">
                                <label>Nova privremena lozinka (PIN):</label>
                                <div className="st-employee-pin-row">
                                    <input
                                        ref={initialPasswordRef}
                                        type="text"
                                        className="st-employee-form-input st-employee-pin-input"
                                        placeholder="Generišite ili unesite novi PIN"
                                        autoComplete="off"
                                        onChange={clearFormError}
                                    />
                                    <button
                                        type="button"
                                        className={`st-employee-generate-pin-btn ${isDarkTheme ? "dark-big-btn" : ""}`}
                                        onClick={handleGenerateInitialPassword}
                                    >
                                        Generiši
                                    </button>
                                </div>
                                <div className="st-employee-form-tagid-hint">
                                    Zaposleni još nije završio prvo podešavanje naloga. Generišite novi PIN i prosledite ga zaposlenom za prijavu.
                                </div>
                            </div>
                        )}

                        <div className="dv-edit-field st-tagid-field">
                            <label>Tag ID:</label>
                            <input
                                ref={tagIdRef}
                                type="text"
                                readOnly
                                className={`st-employee-form-input st-employee-form-input--tag-readonly${tagIdFlash ? " st-employee-form-input--tag-flash" : ""}`}
                                defaultValue={employee?.tagid || ""}
                                placeholder="Prislonite tag na čitač"
                                onBeforeInput={(e) => e.preventDefault()}
                                onPaste={(e) => e.preventDefault()}
                                onCut={(e) => e.preventDefault()}
                                onDrop={(e) => e.preventDefault()}
                            />
                            <div className="st-employee-form-tagid-hint">
                                Dok je forma otvorena, možete prisloniti novi tag koji će se automatski pojaviti ovde.
                            </div>
                        </div>

                        <div className="dv-edit-field">
                            <label>Grupa:</label>
                            <Dropdown
                                dropdownId="employee-group"
                                openDropdownId={openDropdownId}
                                setOpenDropdownId={setOpenDropdownId}
                                addingNewItemId={addingNewItemId}
                                setAddingNewItemId={setAddingNewItemId}
                                valueRef={groupRef}
                                onChange={handleGroupChange}
                                dropdownItems={groupOptions}
                                label="Grupa:"
                            />
                        </div>

                        {isEditMode && (
                            <button
                                type="button"
                                className="st-employee-delete-btn"
                                onClick={handleDeleteClick}
                                aria-label="Obriši korisnika"
                            >
                                <img
                                    src="/cards/kanta.svg"
                                    alt=""
                                    className={isDarkTheme ? "dark-filter" : ""}
                                />
                                <span>Obriši korisnika</span>
                            </button>
                        )}

                        <div className="st-employee-form-footer">
                            <div className="st-employee-form-actions">
                                <AnimatePresence>
                                    {formError && (
                                        <motion.div
                                            key="employee-form-error"
                                            className="st-employee-form-error"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            exit={{ scale: 0 }}
                                            transition={{ duration: 0.19, ease: "easeInOut" }}
                                        >
                                            {formError}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <button
                                    type="button"
                                    className={`dv-edit-save-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                    onClick={handleConfirm}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Čuvanje..." : "Potvrdi"}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: "auto" }}
            />

            <AnimatePresence>
                {isCropModalOpen && cropImageSrc && (
                    <EmployeeImageCropModal
                        key="employee-image-crop"
                        imageSrc={cropImageSrc}
                        onConfirm={handleCropConfirm}
                        onClose={handleCropCancel}
                    />
                )}
                {isDeleteConfirmOpen && (
                    <motion.div
                        className="st-delete-confirm-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.14, ease: "easeInOut" }}
                    >
                        <motion.div
                            className={`st-delete-confirm-backdrop ${isDarkTheme ? "st-delete-confirm-backdrop--dark" : ""}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.14, ease: "easeInOut" }}
                            onClick={() => setIsDeleteConfirmOpen(false)}
                            aria-hidden="true"
                        />
                        <motion.div
                            className="st-delete-confirm-modal-wrap"
                            initial={{ opacity: 0, scale: 0.4 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.4 }}
                            transition={{ duration: 0.14, ease: "easeInOut" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div
                                className={`dv-delete-confirm-modal ${isDarkTheme ? "dark-dv-delete-confirm-modal" : "white-dv-delete-confirm-modal"}`}
                            >
                                <button
                                    type="button"
                                    className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                                    onClick={() => setIsDeleteConfirmOpen(false)}
                                    aria-label="Zatvori"
                                    style={{ position: "absolute", top: "19px", right: "19px" }}
                                >
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                        <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                        <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                    </svg>
                                </button>
                                <h3>Potvrda</h3>
                                <p>
                                    Da li ste sigurni da želite da <b>obrišete</b> korisnika{" "}
                                    <b>{`${employee?.ime || ""} ${employee?.prezime || ""}`.trim()}</b>?
                                </p>
                                <div className="dv-delete-confirm-modal-buttons">
                                    <button
                                        type="button"
                                        className={`dv-delete-confirm-modal-button cancel-btn ${isDarkTheme ? "dark-btn-close" : ""}`}
                                        onClick={() => setIsDeleteConfirmOpen(false)}
                                    >
                                        Odustani
                                    </button>
                                    <button
                                        type="button"
                                        className={`dv-delete-confirm-modal-button confirm-btn ${isDarkTheme ? "dark-big-btn" : ""}`}
                                        onClick={handleDeleteConfirm}
                                    >
                                        Potvrdi
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );

    return createPortal(modalContent, document.body);
};
